"use client";

import { c as _c } from "react/compiler-runtime";
import { storage } from "@/lib/storage";
import { Info } from "lucide-react";
import * as React from "react";
interface GalleryInfoButtonProps {
  className?: string;
}

// Custom hook for storage with SSR safety and cross-tab sync
function useStorage(key: string, initialValue: boolean) {
  // State to store our value - start with true (hidden) while loading
  const [storedValue, setStoredValue] = React.useState<boolean>(true);
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Load initial value from storage
  React.useEffect(() => {
    const loadValue = async () => {
      try {
        // Get from storage by key
        const item = await storage.getItem<boolean>(key);
        // Use stored value or initialValue if none exists
        const value = item !== null ? item : initialValue;
        setStoredValue(value);
      } catch (_) {
        // If error use initialValue
        setStoredValue(initialValue);
      } finally {
        setIsLoaded(true);
      }
    };
    loadValue();
  }, [key, initialValue]);

  // Listen for storage changes (cross-tab sync)
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const newValue = JSON.parse(e.newValue);
          setStoredValue(newValue);
        } catch (__0) {
          // Handle parsing error
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key]);

  // Custom event for same-page sync
  React.useEffect(() => {
    const handleCustomStorageChange = (e_0: CustomEvent) => {
      if (e_0.detail.key === key) {
        setStoredValue(e_0.detail.value);
      }
    };
    window.addEventListener("storageChange", handleCustomStorageChange as EventListener);
    return () => window.removeEventListener("storageChange", handleCustomStorageChange as EventListener);
  }, [key]);

  // Return a wrapped version of useState's setter function that persists the new value to storage
  const setValue = React.useCallback(async (value_0: boolean) => {
    try {
      setStoredValue(value_0);
      // Save to storage
      await storage.setItem(key, value_0);

      // Dispatch custom event for same-page sync
      window.dispatchEvent(new CustomEvent("storageChange", {
        detail: {
          key,
          value: value_0
        }
      }));
    } catch (__1) {
      // Handle storage errors
    }
  }, [key]);
  return [storedValue, setValue, isLoaded] as const;
}
export function GalleryInfoButton(t0) {
  const $ = _c(14);
  const {
    className
  } = t0;
  const [showInfoPopup, setShowInfoPopup] = React.useState(false);
  const [infoAcknowledged, setInfoAcknowledged, isLoaded] = useStorage("gallery-info-acknowledged", false);
  if (!isLoaded) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      setShowInfoPopup(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggleInfoPopup = t1;
  const t2 = `absolute top-3 right-3 z-20 flex items-center h-8 ${className}`;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_1 => {
      if (e_1.key === "Enter" || e_1.key === " ") {
        toggleInfoPopup(e_1 as any);
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `backdrop-blur-sm text-white p-1 rounded-full w-8 h-8 flex items-center justify-center transition-all duration-300 cursor-pointer outline-none ${infoAcknowledged ? "bg-black/20 hover:bg-black/30 opacity-50" : "bg-black/50 hover:bg-black/70"}`;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Info className="w-4 h-4" />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t4) {
    t6 = <div role="button" tabIndex={0} onMouseDown={_temp2} onClick={toggleInfoPopup} onKeyDown={t3} className={t4} aria-label="Show gallery controls info">{t5}</div>;
    $[3] = t4;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t2 || $[6] !== t6) {
    t7 = <div className={t2}>{t6}</div>;
    $[5] = t2;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== setInfoAcknowledged || $[9] !== showInfoPopup) {
    t8 = showInfoPopup && <div className="absolute top-12 right-3 z-20 bg-black/80 backdrop-blur-sm text-white text-sm rounded-lg p-3 max-w-[80%]"><ul className="space-y-1 list-disc list-inside mb-3"><li>Navigation: Tap on left/right sides, swipe or use arrow keys</li><li>Full screen: Double tap to toggle</li><li>Back to exit full screen</li></ul><div role="button" tabIndex={0} onMouseDown={_temp3} onClick={e_3 => {
        e_3.preventDefault();
        e_3.stopPropagation();
        e_3.nativeEvent.stopImmediatePropagation();
        setInfoAcknowledged(true);
        setShowInfoPopup(false);
      }} onKeyDown={e_4 => {
        if (e_4.key === "Enter" || e_4.key === " ") {
          e_4.preventDefault();
          e_4.stopPropagation();
          setInfoAcknowledged(true);
          setShowInfoPopup(false);
        }
      }} className="w-full bg-white/20 hover:bg-white/30 text-white text-xs py-2 px-3 rounded transition-colors cursor-pointer text-center outline-none">Ok understood! Hide this</div></div>;
    $[8] = setInfoAcknowledged;
    $[9] = showInfoPopup;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
function _temp3(e_2) {
  e_2.preventDefault();
  e_2.stopPropagation();
}
function _temp2(e_0) {
  e_0.preventDefault();
  e_0.stopPropagation();
}
function _temp(s) {
  return !s;
}